import { useLatest } from '@library/hooks'
import { getIsolineLabels, getSparsedPoints } from '@library/model'
import type {
  AttributeBoundary,
  GridLabel,
  GridType,
  ICanvasSize,
  MapHeader,
  Nullable,
  PaletteBody,
  Range2D,
  ScaleType,
} from '@library/types'
import type { Quadtree } from 'd3'
import { quadtree } from 'd3'
import { useMemo } from 'react'

export const useIsolineLabels = (
  mapHeader: MapHeader | undefined,
  mapBody: GridType['body'],
  isolineBase: number,
  isolineStep: number,
  canvas: Nullable<ICanvasSize>,
  origin: Range2D,
  scale: ScaleType,
  palette: PaletteBody,
  attributeBoundary?: AttributeBoundary,
): GridLabel[] => {
  const tree = useMemo<Quadtree<GridLabel>>(() => {
    if (!mapHeader) return quadtree<GridLabel>([])

    const labels = getIsolineLabels(
      mapHeader,
      mapBody,
      isolineBase,
      isolineStep,
      palette,
      attributeBoundary,
    )

    return quadtree<GridLabel>(
      labels,
      label => label.x,
      label => label.y,
    )
  }, [
    mapHeader,
    mapBody,
    isolineBase,
    isolineStep,
    palette,
    attributeBoundary?.start,
    attributeBoundary?.stop,
  ])

  const canvasRef = useLatest(canvas)

  return useMemo(() => {
    return getSparsedPoints(canvasRef.current, origin, scale, tree)
  }, [origin, scale, tree])
}
